import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, TrendingUp, Award } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import AppLayout from "@/components/AppLayout";
import BilingualText from "@/components/BilingualText";
import ReadinessMeter from "@/components/ReadinessMeter";
import BadgesGrid from "@/components/BadgesGrid";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { loadGuestAttempts } from "@/lib/localProgress";

const SKILL_NAMES = {
  reading: { cn: "阅读", en: "Reading" },
  vocabulary: { cn: "词汇", en: "Vocabulary" },
  grammar: { cn: "语法", en: "Grammar" },
  listening: { cn: "听力", en: "Listening" },
  writing: { cn: "写作", en: "Writing" },
  speaking: { cn: "口语", en: "Speaking" },
};

export default function Progress() {
  const { isAuthed, isGuest } = useAuth();
  const [summary, setSummary] = useState({
    readiness: 0, skill_avg: {}, weak_areas: [], total_attempts: 0, streak_days: 0,
  });
  const [series, setSeries] = useState([]);
  const [recent, setRecent] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isAuthed) {
        const [s, h, b] = await Promise.all([
          api.get("/progress/summary"),
          api.get("/progress/history", { params: { days: 14 } }),
          api.get("/badges"),
        ]);
        setSummary(s.data);
        setSeries(h.data.series || []);
        setRecent(h.data.recent_attempts || []);
        setBadges(b.data.badges || []);
      } else if (isGuest) {
        const attempts = loadGuestAttempts();
        const [g, b] = await Promise.all([
          api.post("/progress/guest-summary", { attempts }),
          api.post("/badges/guest", { attempts }),
        ]);
        setSummary({ ...g.data, streak_days: 0 });
        setBadges(b.data.badges || []);
        // Build local series
        const byDate = {};
        attempts.forEach((a) => {
          if (a.score == null || !a.created_at) return;
          const d = a.created_at.slice(0, 10);
          if (!byDate[d]) byDate[d] = [];
          byDate[d].push(a.score);
        });
        const dates = Object.keys(byDate).sort().slice(-14);
        setSeries(dates.map((d) => ({
          date: d.slice(5),
          score: Math.round(byDate[d].reduce((a, b) => a + b, 0) / byDate[d].length),
        })));
        setRecent(attempts.slice(-20).reverse());
      }
    } finally { setLoading(false); }
  }, [isAuthed, isGuest]);

  useEffect(() => { load(); }, [load]);

  const skillBars = Object.entries(summary.skill_avg || {}).map(([k, v]) => ({
    skill: SKILL_NAMES[k]?.en || k,
    cn: SKILL_NAMES[k]?.cn || k,
    score: Math.round(v),
  }));

  const displaySeries = series.map((row) => {
    // Convert full ISO dates to MM-DD for compact axis
    const date = row.date && row.date.length > 5 ? row.date.slice(5) : row.date;
    return { ...row, date };
  });

  if (loading) {
    return (
      <AppLayout>
        <div className="grid place-items-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div data-testid="progress-root" className="space-y-10">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-100 to-emerald-100 grid place-items-center">
            <TrendingUp className="w-5 h-5 text-sky-600" />
          </span>
          <BilingualText cn="学习进度" en="Your progress" size="hero" />
        </div>

        {/* Top row */}
        <section className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-4">
            <ReadinessMeter
              value={summary.readiness}
              subtitle={`${summary.total_attempts || 0} attempts · ${summary.total_attempts || 0} 次练习`}
            />
          </div>
          <div className="md:col-span-8 card-plush p-6">
            <BilingualText cn="各项技能得分" en="Skill scores" size="md" />
            <div className="mt-6" style={{ width: "100%", height: 240 }}>
              {skillBars.length ? (
                <ResponsiveContainer>
                  <BarChart data={skillBars}>
                    <CartesianGrid strokeDasharray="4 6" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="skill" tick={{ fontSize: 12, fill: "#64748b" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#64748b" }} />
                    <Tooltip
                      formatter={(v) => [`${v}`, "Score"]}
                      contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }}
                    />
                    <Bar dataKey="score" fill="#3b82f6" radius={[10, 10, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full grid place-items-center text-slate-400 text-sm">
                  Practise a few questions to see your scores.
                </div>
              )}
            </div>
          </div>
        </section>

        {/* History chart */}
        <section className="card-plush p-6">
          <div className="flex items-center justify-between">
            <BilingualText cn="最近成绩趋势" en="Recent score trend" size="md" />
            <span className="text-xs text-slate-400 font-semibold uppercase tracking-widest">
              14 days · 14 天
            </span>
          </div>
          <div className="mt-4" style={{ width: "100%", height: 260 }}>
            {displaySeries.length ? (
              <ResponsiveContainer>
                <LineChart data={displaySeries}>
                  <CartesianGrid strokeDasharray="4 6" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748b" }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#64748b" }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    dot={{ r: 4, fill: "#3b82f6" }}
                  />
                  {["reading", "vocabulary", "grammar", "listening", "writing", "speaking"].map((s, i) => (
                    <Line
                      key={s}
                      type="monotone"
                      dataKey={s}
                      stroke={["#10b981", "#8b5cf6", "#f59e0b", "#f43f5e", "#0ea5e9", "#ec4899"][i]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-slate-400 text-sm">
                <BilingualText
                  cn="练习几次后，这里就会出现你的成绩趋势。"
                  en="Practise a few times to see your trend."
                  size="sm"
                  className="items-center"
                />
              </div>
            )}
          </div>
        </section>

        {/* Badges */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-yellow-100 to-amber-100 grid place-items-center">
              <Award className="w-5 h-5 text-amber-600" />
            </span>
            <BilingualText cn="徽章收集" en="Achievements" size="lg" />
            <span className="ml-auto text-sm font-bold text-slate-500">
              {badges.filter((b) => b.earned).length} / {badges.length}
            </span>
          </div>
          {badges.length ? (
            <BadgesGrid badges={badges} />
          ) : (
            <div className="card-plush p-10 text-center text-slate-400">Loading…</div>
          )}
        </section>

        {/* Recent attempts */}
        <section className="card-plush p-6">
          <BilingualText cn="最近练习记录" en="Recent activity" size="md" />
          {recent.length ? (
            <ul className="mt-4 divide-y divide-slate-100">
              {recent.slice(0, 12).map((a, i) => {
                const sk = SKILL_NAMES[a.skill] || { cn: a.skill, en: a.skill };
                const dt = (a.created_at || "").slice(0, 16).replace("T", " ");
                const scoreDisplay = a.score != null ? Math.round(a.score) : (a.correct ? "✓" : "×");
                return (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className="py-3 flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="badge-soft shrink-0">{a.level}</span>
                      <div className="flex flex-col min-w-0">
                        <span className="cn text-sm font-bold text-slate-900">
                          {sk.cn}
                        </span>
                        <span className="text-xs text-slate-500">{sk.en} · {dt}</span>
                      </div>
                    </div>
                    <span
                      className={
                        "rounded-full px-3 py-1 text-sm font-black " +
                        (a.correct === false
                          ? "bg-rose-50 text-rose-700"
                          : a.correct === true
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-sky-50 text-sky-700")
                      }
                    >
                      {scoreDisplay}
                    </span>
                  </motion.li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-slate-400">
              <span className="cn">开始练习，这里就会显示记录。</span> Start practising and your history will show here.
            </p>
          )}
        </section>

        <div className="text-right">
          <Button asChild variant="outline" className="rounded-full font-bold">
            <Link to="/dashboard">
              <span className="cn mr-1">返回首页</span>Back to dashboard
            </Link>
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
